import '../../css/theme/profile/profile-game-history.scss'
import {GameData, GameSettings, PlayerGameData} from '@colonist/game-base-types'
import {BigId, LobbyImage} from '@colonist/types'
import {ArrayUtils} from '../../../../shared/utils/ArrayUtils'
import {navigateToPage, turnSecondsInToTimerFormat} from '../ui_utils/ClientUtils'
import {
    createTDIn,
    createImgIn,
    createTRIn,
    createLinkIn,
    createTableIn,
    createDivIn,
    createTHIn,
    createSpanIn,
    setElementInnerHTML,
    setElementOnClick,
} from '../ui_utils/DomUtils'
import {DELETED_USER_USERNAME} from '../../../../shared/UserState'
import {CA} from '../analytics/Analytics'
import {AnalyticEventCategory, AnalyticEventType} from '../../../../shared/entity_datas/EntityDatas'
import {GameModeDataController} from '@colonist/shared-types'
import {MapDataController} from '../../../../shared/map_data/MapDataController'
import {getNestedTranslation, TranslationObject} from '@colonist/i18n'
import {getDiceSettingText, getExtensionSettingText, getScenarioSettingText, PlayerEnums} from '../../../../shared/GameEnums'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'
import {TimeUtils} from '@colonist/utils'
import {PlayerColor} from '@colonist/game-states'
import {getProfilePopupElementId, UIViewProfilePopup} from './UIViewProfilePopup.ts'

export class UIProfileGameHistoryController {
    private readonly gameHistoryTableHead: HTMLTableSectionElement
    protected readonly gameHistoryTableBody: HTMLTableSectionElement
    private readonly activeTableInfoIds: string[] = []

    private readonly isPopup: boolean

    constructor(isPopup: boolean = false) {
        const gameHistoryTableHeadId = getProfilePopupElementId('game-history-table-head', isPopup)
        const gameHistoryTableBodyId = getProfilePopupElementId('game-history-table-body', isPopup)
        this.gameHistoryTableHead = document.getElementById(gameHistoryTableHeadId) as HTMLTableSectionElement
        this.gameHistoryTableBody = document.getElementById(gameHistoryTableBodyId) as HTMLTableSectionElement
        this.isPopup = isPopup
    }
    update(games: GameData[], username: string, profileUserId: BigId, addOpponentsColumn: boolean = false) {
        this.resetHistoryTable()
        this.createGameHistoryTableHead(addOpponentsColumn)
        games.reverse()
        for(const game of games) {
            this.createGameRow(game, username, profileUserId, addOpponentsColumn)
            this.createGameDetailsRow(game)
        }
    }

    private resetHistoryTable() {
        setElementInnerHTML(this.gameHistoryTableHead, {key: 'strings:colonistCommon.utils.emptyString'})
        setElementInnerHTML(this.gameHistoryTableBody, {key: 'strings:colonistCommon.utils.emptyString'})
    }

    createGameDetailsRow(game: GameData): void {
        const tr = createTRIn(this.gameHistoryTableBody, this.getDetailsId(game.id))
        tr.style.display = 'none'
        const td = createTDIn(tr, 'game-details-td')
        td.colSpan = 6
        const gameDetailsGrid = createDivIn(td, 'game-details-grid')
        game.setting = GameSettings.deserialize(game.setting)
        UIProfileGameHistoryController.createGameDetailsPlayersIn(game, gameDetailsGrid)
        UIProfileGameHistoryController.createGameDetailsSettingsIn(game, gameDetailsGrid)
    }

    private createGameHistoryTableHead(addOpponentsColumn: boolean) {
        const rowHead = this.gameHistoryTableHead.insertRow()
        createTHIn(rowHead, undefined, {key: 'strings:profilePage.history.table.date.title'})
        createTHIn(rowHead, undefined, {key: 'strings:profilePage.history.table.rank.title'})
        if(addOpponentsColumn) createTHIn(rowHead, 'center', {key: 'strings:profilePage.history.table.opponents.title'})
        createTHIn(rowHead, 'right', {key: 'strings:profilePage.history.table.duration.title'})
        createTHIn(rowHead, 'center', {key: 'strings:profilePage.history.table.replay.title'})
    }

    private createGameRow(game: GameData, encodedUsername: string, profileUserId: BigId, addOpponentsColumn: boolean = false) {
        const username = decodeURIComponent(encodedUsername)
        const tr = createTRIn(this.gameHistoryTableBody, '')
        const date = TimeUtils.convertTimestampToDateTimeText(game.startTime)

        let rank = ''
        let vp = ''

        for(const player of game.players) {
            if(player.username.toLowerCase() == username.toLowerCase()) {
                rank = String(player.rank) + '/' + String(game.players.length)
                vp = String(player.points) + '/' + String(game.setting.victoryPointsToWin)
                continue
            }
        }

        createTDIn(tr, undefined, date)
        createTDIn(tr, undefined, {key: 'strings:colonistCommon.utils.#buffer', options: {value: rank}})
        if(addOpponentsColumn) UIProfileGameHistoryController.addOpponentsColumn(game, tr)
        createTDIn(tr, 'right', {key: 'strings:colonistCommon.utils.#buffer', options: {value: turnSecondsInToTimerFormat(game.duration / 1000)}})
        this.addReplayColumn(game, profileUserId, tr)

        // eslint-disable-next-line no-restricted-syntax
        tr.onclick = (event) => {
            const clickedElement = event.target as HTMLElement | null
            if(clickedElement == undefined) return

            const isReplayLinkClicked = clickedElement.closest('a') !== null
            if(isReplayLinkClicked) return

            const thisGamesId = this.getDetailsId(game.id)
            const details = document.getElementById(thisGamesId)
            if(details == undefined) return
            if(this.activeTableInfoIds.indexOf(thisGamesId) > -1) {
                details.style.display = 'none'
                ArrayUtils.removeElementFromArray(this.activeTableInfoIds, thisGamesId)
                return
            }
            details.style.display = 'table-row'
            this.activeTableInfoIds.push(thisGamesId)
        }
    }

    private static addOpponentsColumn(game: GameData, tr: HTMLTableRowElement) {
        const opponentsTD = createTDIn(tr, 'center opponents-td')

        for(const player of game.players) {
            UIProfileGameHistoryController.createUsernameElement(player, opponentsTD)
        }
    }

    private addReplayColumn(game: GameData, profileUserId: BigId, tr: HTMLTableRowElement) {
        const replayTD = createTDIn(tr, 'center')
        if(!game.hasReplay) return

        const profileColorInGame = game.players.find((player) => player.userId === profileUserId)?.playerColor
        if(profileColorInGame === undefined) return

        const replayUrl = this.getReplayUrl(game, profileColorInGame)
        const replayLink = createLinkIn(replayTD, '', {key: 'strings:colonistCommon.utils.emptyString'}, '')
        createImgIn(replayLink, LobbyImage.ReplayIcon)
        setElementOnClick(replayLink, () => {
            void CA.sendAnalyticEvent({
                type: AnalyticEventType.ReplayClickedViewFromProfileGameHistory,
                category: AnalyticEventCategory.Replay,
                stringValue: game.id,
            })
            navigateToPage(replayUrl, true)
        }, true)
    }

    private getReplayUrl(game: GameData, profileColorInGame: PlayerColor): string {
        if(UIUtilsEjsVariables.isUserOnDiscord && this.isPopup) return `https://colonist.io/replay?gameId=${game.id}&playerColor=${profileColorInGame}`
        if(UIUtilsEjsVariables.isUserOnDiscord) return `/replay?gameId=${game.id}&playerColor=${profileColorInGame}`

        const replayUrl = new URL(window.location.href)
        replayUrl.pathname = UIUtilsEjsVariables.isUserOnMobileApp ? '/mobile/replay' : '/replay'
        replayUrl.searchParams.set('gameId', game.id.toString())
        replayUrl.searchParams.set('playerColor', profileColorInGame.toString())
        return replayUrl.href
    }

    protected getDetailsId(gameId: BigId): string {
        return getProfilePopupElementId(`table_${gameId}`, this.isPopup)
    }

    private static createUsernameElement(player: PlayerGameData, parent: HTMLElement) {
        const username = player.isHuman ? player.username : 'Bot'
        const usernameTranslation: TranslationObject = {key: 'strings:colonistCommon.utils.#buffer', options: {value: username}}
        const isDeletedUser = player.username === DELETED_USER_USERNAME
        const isPlayerProfileLinkable = !isDeletedUser && player.isHuman
        if(!isPlayerProfileLinkable) {
            createSpanIn(parent, '', usernameTranslation)
        } else {
            const linkElement = createLinkIn(parent, '', usernameTranslation, '')
            setElementOnClick(linkElement, () => UIViewProfilePopup.init(username), true, true)
        }
    }

    private static createGameDetailsPlayersIn(game: GameData, parent: HTMLElement) {
        const container = createDivIn(parent, 'game-details-container')
        const table = createTableIn(container, 'game-details-table')
        const tableHead = table.createTHead()
        const tableRowHead = tableHead.insertRow()
        createTHIn(tableRowHead, 'center', {key: 'strings:leaderboardPage.tableHeaders.player'})
        createTHIn(tableRowHead, 'center', {key: 'strings:profilePage.history.table.rank.title'})
        createTHIn(tableRowHead, 'center', {key: 'strings:profilePage.history.table.vp.title'})
        createTHIn(tableRowHead, 'center', {key: 'strings:profilePage.history.table.finished.title'})
        createTHIn(tableRowHead, 'center', {key: 'strings:profilePage.history.table.karmaPenalty.title'})

        const tableBody = table.createTBody()
        const playersByRank = game.players.sort((a, b) => a.rank - b.rank)
        for(const playerData of playersByRank) {
            const playerRow = tableBody.insertRow()

            const usernameTD = createTDIn(playerRow, 'center')
            UIProfileGameHistoryController.createUsernameElement(playerData, usernameTD)
            createTDIn(playerRow, 'center', {key: 'strings:colonistCommon.utils.#buffer', options: {value: String(playerData.rank) + '/' + String(game.players.length)}})
            createTDIn(playerRow, 'center', {key: 'strings:colonistCommon.utils.#buffer', options: {value: String(playerData.points) + '/' + String(game.setting.victoryPointsToWin)}})
            const finishedTD = createTDIn(playerRow, 'center')
            const finishedIcon = playerData.finished ? LobbyImage.CheckMark : LobbyImage.XMark
            createImgIn(finishedTD, finishedIcon)

            const quitWithPenaltyTd = createTDIn(playerRow, 'center')
            const quitWithPenaltyIcon = playerData.quitWithPenalty ? LobbyImage.CheckMark : LobbyImage.XMark
            createImgIn(quitWithPenaltyTd, quitWithPenaltyIcon)
        }
    }

    protected static createGameDetailsSettingsIn(game: GameData, parent: HTMLElement) {
        const gameModeData = GameModeDataController.getModeDataForMode(game.setting.modeSetting)
        if(gameModeData === undefined) return

        const container = createDivIn(parent, 'game-details-container')
        const table = createTableIn(container, 'game-details-table')
        const tableBody = table.createTBody()

        const tableRow1 = tableBody.insertRow()
        createTDIn(tableRow1, undefined, {key: 'strings:game.settings.gameInfoTab.id.value', options: {id: game.setting.id}})
        createTDIn(tableRow1, undefined, getNestedTranslation({key: 'strings:game.settings.gameInfoTab.map.value'}, {mapName: MapDataController.getMapName(game.setting.mapSetting)}))

        const tableRow2 = tableBody.insertRow()
        createTDIn(tableRow2, undefined, getNestedTranslation({key: 'strings:game.settings.gameInfoTab.gameMode.value'}, {modeName: gameModeData.name}))
        createTDIn(tableRow2, undefined, getNestedTranslation({key: 'strings:game.settings.gameInfoTab.extension.value'}, {extensionName: getExtensionSettingText(game.setting.extensionSetting)}))

        const tableRow3 = tableBody.insertRow()
        createTDIn(tableRow3, undefined, getNestedTranslation({key: 'strings:game.settings.gameInfoTab.scenario.value'}, {scenarioName: getScenarioSettingText(game.setting.scenarioSetting)}))
        createTDIn(tableRow3, undefined, getNestedTranslation({key: 'strings:game.settings.gameInfoTab.botSpeed.value'}, {botSpeed: PlayerEnums.getBotSpeedSettingText(game.setting.botSpeed)})) // This data isn't mandatory. Added to make symmetric

        const tableRow4 = tableBody.insertRow()
        createTDIn(tableRow4, undefined, getNestedTranslation({key: 'strings:game.settings.gameInfoTab.turnTimer.value'}, {turnTimer: PlayerEnums.getSpeedSettingText(game.setting.gameSpeed)}))
        createTDIn(tableRow4, undefined, {key: 'strings:game.settings.gameInfoTab.vpToWin.value', options: {value: game.setting.victoryPointsToWin}})

        const tableRow5 = tableBody.insertRow()
        const karmaTranslation: TranslationObject = {key: `strings:game.settings.gameInfoTab.karma.options.${game.setting.karmaActive ? 'Active' : 'Inactive'}`}
        const friendlyRobberTranslation: TranslationObject = {key: `strings:game.settings.gameInfoTab.friendlyRobber.options.${game.setting.friendlyRobber ? 'Active' : 'Inactive'}`}
        createTDIn(tableRow5, undefined, getNestedTranslation({key: 'strings:game.settings.gameInfoTab.karma.value'}, {karmaState: karmaTranslation}))
        createTDIn(tableRow5, undefined, getNestedTranslation({key: 'strings:game.settings.gameInfoTab.friendlyRobber.value'}, {friendlyRobberState: friendlyRobberTranslation}))

        const tableRow6 = tableBody.insertRow()
        const bankCardsTranslation: TranslationObject = {key: `strings:game.settings.gameInfoTab.bankCards.options.${game.setting.hideBankCards ? 'Hidden' : 'Showing'}`}
        createTDIn(tableRow6, undefined, getNestedTranslation({key: 'strings:game.settings.gameInfoTab.bankCards.value'}, {bankCardsVisibility: bankCardsTranslation}))
        createTDIn(tableRow6, undefined, getNestedTranslation({key: 'strings:game.settings.gameInfoTab.dice.value'}, {diceSetting: getDiceSettingText(game.setting.diceSetting)}))

        const tableRow7 = tableBody.insertRow()
        createTDIn(tableRow7, undefined, {key: 'strings:game.settings.gameInfoTab.cardDiscardLimit.value', options: {value: game.setting.cardDiscardLimit}})
        createTDIn(tableRow7, undefined, {key: 'strings:profilePage.history.table.turns.value', options: {turnCount: game.turnCount}})
    }
}
